import React, { useEffect, useState } from 'react';
import { Delete } from '@mui/icons-material';
import { TextField, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Controller, useForm } from 'react-hook-form';
import { PlatformGroupBody, PlatformGroupResponse, TargetPlatform } from '../../../API';
import { usePlatformGroupsAPI } from '../../../hooks/API/Config/usePlatformGroupsAPI';
import { useConfirm, useLocales, useNotifications, useTheme } from '../../../hooks';
import Button from '../../shared/Button';
import Drawer from '../../shared/Drawer';
import { PlatformMultiSelect } from '../../shared/Platforms/PlatformMultiSelect';
import { platformGroupsTestIds } from '../../shared/TestsIds';

const useStyles = makeStyles()((theme) => ({
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4)
  },
  footerButtons: {
    display: 'flex',
    gap: theme.spacing(4)
  },
  footerButton: {
    minWidth: 160
  }
}));

interface FormValues {
  name: string;
  platforms: TargetPlatform[];
}

const DEFAULT_VALUES: FormValues = { name: '', platforms: [] };

interface PlatformGroupFormProps {
  open: boolean;
  platformGroup?: PlatformGroupResponse;
  canManage: boolean;
  onClose: (saved: boolean) => void;
}

function PlatformGroupForm({ open, platformGroup, canManage, onClose }: PlatformGroupFormProps): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const { notifyError } = useNotifications();
  const { formControlColor } = useTheme();
  const { confirm } = useConfirm();
  const platformGroupsAPI = usePlatformGroupsAPI();
  const isEditing = !!platformGroup;
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { control, handleSubmit, reset } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (open) {
      reset(platformGroup ? { name: platformGroup.name, platforms: platformGroup.platforms } : { ...DEFAULT_VALUES });
    }
  }, [open, platformGroup, reset]);

  const onSubmit = async (values: FormValues) => {
    const body: PlatformGroupBody = { name: values.name.trim(), platforms: values.platforms };

    setIsSaving(true);
    try {
      if (isEditing && platformGroup) {
        await platformGroupsAPI.update(platformGroup.id, body);
      } else {
        await platformGroupsAPI.create(body);
      }
      onClose(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        notifyError(t('platform_groups.error_duplicate_name'));
      } else {
        notifyError(t('platform_groups.error_save_failed'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (!platformGroup) return;

    const confirmed = await confirm({
      confirmText: t('general.confirm_delete'),
      confirmColor: 'error',
      cancelText: t('general.cancel'),
      body: t('platform_groups.delete_confirm', { name: platformGroup.name })
    });
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await platformGroupsAPI.remove(platformGroup.id);
      onClose(true);
    } catch {
      notifyError(t('platform_groups.error_delete_failed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const headerRight = isEditing && (
    <Button
      color="error"
      endIcon={<Delete />}
      loading={isDeleting}
      onClick={onDelete}
      disabled={!canManage || isSaving}
      data-testid={platformGroupsTestIds.deleteButton}
    >
      {t('general.delete')}
    </Button>
  );

  const footerLeft = (
    <div className={classes.footerButtons}>
      <Button
        className={classes.footerButton}
        color="primary"
        loading={isSaving}
        onClick={handleSubmit(onSubmit)}
        disabled={!canManage || isDeleting}
        data-testid={platformGroupsTestIds.saveButton}
      >
        {t('general.save')}
      </Button>
    </div>
  );

  const footerRight = (
    <Button
      className={classes.footerButton}
      color="grey"
      disabled={isDeleting}
      onClick={() => onClose(false)}
      data-testid={platformGroupsTestIds.cancelButton}
    >
      {t('general.cancel')}
    </Button>
  );

  return (
    <Drawer
      open={open}
      onClose={() => onClose(false)}
      headerLeft={
        <Typography variant="h6">
          {isEditing ? t('platform_groups.edit_group', { name: platformGroup?.name }) : t('platform_groups.new_group')}
        </Typography>
      }
      headerRight={headerRight}
      footerLeft={footerLeft}
      footerRight={footerRight}
    >
      <div className={classes.formBody}>
        <Controller
          control={control}
          name="name"
          rules={{ required: t('platform_groups.group_name_required') }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t('platform_groups.group_name')}
              required
              error={!!error}
              helperText={error?.message}
              fullWidth
              color={formControlColor}
              inputProps={{ 'data-testid': platformGroupsTestIds.nameInput }}
            />
          )}
        />

        <Controller
          control={control}
          name="platforms"
          rules={{ validate: (platforms) => platforms.length > 0 || t('platform_groups.platforms_required') }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <PlatformMultiSelect
                label={t('platform_groups.platforms_field')}
                value={field.value}
                onChange={field.onChange}
              />
              {error && (
                <Typography variant="caption" color="error" data-testid={platformGroupsTestIds.platformsError}>
                  {error.message}
                </Typography>
              )}
            </div>
          )}
        />
      </div>
    </Drawer>
  );
}

export default PlatformGroupForm;
